import * as R from 'ramda';
import { ThunkAction } from 'redux-thunk';
import Api from '@services/api';
import Storage from '@services/storage';

import type { StoreState } from '@common/types/store';
import type {
  AccessDataNetwork,
  AccessDataFunction,
  AccessData,
  TrialGenerationStatus,
  TrialLeadOverrides,
  TrialResearchStatus,
} from '../types';
import { Dispatch } from 'redux';

export const AUTHENTICATION_LOGIN = 'auth/LOGIN';
export const AUTHENTICATION_FAILED = 'auth/FAILED';
export const LOGOUT_USER = 'core/LOGOUT_USER';
export const AUTHENTICATION_ONBOARD_ORGANISATION = 'auth/ONBOARD_ORGANISATION';

type SessionTokens = { access_token: string; refresh_token: string };

/** Persists a session the way every login path expects it (cookies read by the API service). */
const storeSession = ({ access_token: accessToken, refresh_token: refreshToken }: SessionTokens, rememberMe = false) => {
  const expiration = rememberMe ? [10, 'years'] : undefined;

  Storage.setCookie('access_token', accessToken, [59, 'minutes']);
  Storage.setCookie('refresh_token', refreshToken, expiration);
  Storage.setCookie('auth_token', refreshToken, expiration);
  Storage.setCookie('original_domain', window.location.host, expiration);
};

export const authenticate = (
  credentials: { username: string, password: string, remember_me: boolean },
) => async (dispatch: Dispatch) => {
  try {
    const { data } = await Api.post<{ data: SessionTokens & { last_login: string | null; temporary_password: boolean } }>(
      '/v2/authenticate',
      {
        username: credentials.username,
        password: credentials.password,
      },
      false,
    );

    storeSession(data, credentials.remember_me);

    return dispatch({
      type: AUTHENTICATION_LOGIN,
      lastLogin: data.last_login,
      temporaryPassword: data.temporary_password,
    });
  } catch (err) {
    dispatch({ type: AUTHENTICATION_FAILED });

    throw err;
  }
};

// --- Trial generator (landing-page flow) -----------------------------------

type ResearchResponse = { data: TrialResearchStatus };
type GenerationResponse = { data: TrialGenerationStatus };

/** Anonymous research on the landing page: 202 + token when started, 200 when reused. */
export const startTrialResearch = (domain: string, languageLocale: 'nl' | 'en') => Api.post<ResearchResponse>(
  '/v3/trial-research',
  { domain, language_locale: languageLocale },
  false,
);

export const getTrialResearch = (token: string) => Api.get<ResearchResponse>(`/v3/trial-research/${token}`, false);

/** Organisation-scoped research for the dashboard onboarding form (Flow B). */
export const startOrganisationTrialResearch = (organisationId: string, domain: string) => Api.post<ResearchResponse>(
  `/v3/organisations/${organisationId}/trial-research`,
  { domain },
);

export const getOrganisationTrialResearch = (organisationId: string) => Api.get<ResearchResponse>(
  `/v3/organisations/${organisationId}/trial-research`,
);

export const getTrialGeneration = (organisationId: string) => Api.get<GenerationResponse>(
  `/v3/organisations/${organisationId}/trial-generation`,
);

export type CreateTrialWithResearchPayload = TrialLeadOverrides & {
  email: string;
  first_name: string;
  password: string;
  research_token: string;
};

type CreateTrialResponse = SessionTokens & {
  success: boolean;
  organisation_id: string;
  generation: TrialGenerationStatus;
  email_verified: boolean;
};

/**
 * Landing-page signup: creates the organisation and the user, starts the
 * build and opens a session right away (the mail is only the way back in).
 */
export const createTrialWithResearch = (payload: CreateTrialWithResearchPayload) => async (dispatch: Dispatch) => {
  const response = await Api.post<CreateTrialResponse>('/v3/create-trial', payload, false);

  storeSession(response);

  dispatch({
    type: AUTHENTICATION_LOGIN,
    lastLogin: null,
    temporaryPassword: false,
  });

  return response;
};

export const fetchOrganisations = () => async (dispatch: Dispatch) => {
  const response = await Api.get('/v1/organisations');

  dispatch({
    type: 'auth/RECEIVE_ORGANISATIONS',
    items: response.data,
  });

  return response;
};

// @ts-expect-error
export const setupTrial = (organisationId, values) => async (dispatch) => {
  // industry_set / domain select the generator on the API; without them the
  // legacy preset clone runs.
  const payload = R.reject(R.isNil, R.pick([
    'name', 'theme', 'company_size', 'industry',
    'domain', 'industry_set', 'locations', 'modules_of_interest',
  ], values));

  const promises = [Api.post(`/v3/organisations/${organisationId}/setup-trial`, payload)];

  if (values.brand_icon) {
    const fd = new FormData();
    fd.append('brand_icon', values.brand_icon);

    promises.push(Api.post(`/v2/organisations/${organisationId}/picture`, fd));
  }

  const [{ data: organisation }, picture] = await Promise.all(promises);

  if (picture) {
    // @ts-expect-error
    organisation.brand_icon = `${picture.data.brand_icon}?update=${new Date().getTime()}`;
  }

  return dispatch({
    type: AUTHENTICATION_ONBOARD_ORGANISATION,
    organisation,
  });
};

export const createTrial = (email: string) => Api.post('/v3/create-trial', { email }, false);

type ActualGetAccessData = ThunkAction<
Promise<AccessData>,
StoreState,
unknown,
any
>;

type AccessDataApiResponse = {
  data: {
    organisation_name: string;
    organisation_brand_icon: string;
    organisation_theme: {
      login_image_url: string | null;
    };
    functions: AccessDataFunction[];
    networks: AccessDataNetwork[];
  };
};

export const getAccessData = (id: string): ActualGetAccessData => async () => {
  const { data } = await Api.get<AccessDataApiResponse>(`/v1/access-requests/prefetch?access_request_code=${id}`, false);

  return {
    networks: data.networks,
    functions: data.functions,
    name: data.organisation_name,
    brand_icon: data.organisation_brand_icon,
    theme: data.organisation_theme,
  };
};

export type RequestValues = {
  first_name: string;
  last_name: string;
  email: string;
  network_id: string;
  function_id: string;
};

export const createAccessRequest = (id: string, values: RequestValues) => async () => {
  const { is_new: isNew } = await Api.post<{ is_new: boolean }>(`/v1/access-requests?access_request_code=${id}`, values, false);

  return isNew;
};
